import {ShowMore, type ShowMoreRef, type ShowMoreToggleLinesFn} from '@re-dev/react-truncate'
import Rating from 'react-rating'

import starEmptySvg from '@/assets/icons/star/empty.svg'
import starFullSvg from '@/assets/icons/star/full.svg'
import type {FullProduct} from '@/models/productModel'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {formatLocaleDateTime} from '@/utils/datetime'
import roundToNDecimal from '@/utils/numbers/roundToNDecimals'
import style from '@/views/Product/Product.module.scss'

const ProductReview = memo(function ProductReview({
  review,
}: {
  review: FullProduct['reviews'][number]
}) {
  const ref = useRef<ShowMoreRef>(null)

  const toggleLines: ShowMoreToggleLinesFn = e => {
    ref.current?.toggleLines(e)
  }

  return (
    <div className={style.Review}>
      <div className={style.Info}>
        <h3 className={style.Author}>{review.author || 'Anonymous User'}</h3>
        <div className={style.Time}>
          <VisuallyHidden>Reviewed on:</VisuallyHidden>
          {formatLocaleDateTime(review.timestamp, 'en-US', {
            dateStyle: 'short',
          })}
        </div>
        <div>
          <div aria-hidden='true'>
            <Rating
              readonly
              initialRating={roundToNDecimal(review.rating, 1)}
              emptySymbol={<img src={starEmptySvg} alt='' />}
              fullSymbol={<img src={starFullSvg} alt='' />}
            />
          </div>
          <VisuallyHidden>{`Rating: ${roundToNDecimal(review.rating, 1)} out of 5`}</VisuallyHidden>
        </div>
      </div>
      <div className={style.Content}>
        <ShowMore
          ref={ref}
          lines={3}
          more={
            <button
              onClick={toggleLines}
              className={style.ReadMoreLess}
              aria-label='Expand review text'
            >
              …read more
            </button>
          }
          less={
            <button
              onClick={toggleLines}
              className={style.ReadMoreLess}
              aria-label='Collapse review text'
            >
              …read less
            </button>
          }
        >
          {review.content}
        </ShowMore>
      </div>
    </div>
  )
})

export default function ProductReviews({reviews}: {reviews: FullProduct['reviews']}) {
  return (
    <div className={style.ProductReviews} {...skipTargetProps('reviews')}>
      <h2 className={style.Heading}>Top User Reviews</h2>
      {reviews.length === 0 && <p>No reviews yet, be the first to review this product!</p>}
      {reviews.map(review => (
        <ProductReview key={review.id} review={review} />
      ))}
    </div>
  )
}
